import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header';
import CategorySidebar from '../../components/CategorySidebar';
import Footer from '../../components/Footer';
import RequirementModal from '../../components/RequirementModal';
import BecomeSupplierModal from '../../components/BecomeSupplierModal';
import FindBuyerModal from '../../components/FindBuyerModal';
import ProductDetailsModal from '../../components/ProductDetailsModal';
import LoginModal from '../../components/LoginModal';
import RegisterModal from '../../components/RegisterModal';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { productsApi, sellersApi, brandsApi, favoritesApi, categoriesApi, rfqApi } from '../../services/apiService';
import { getPlanConfig, shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import { SOCKET_URL as API_BASE } from '../../config/api';
import './HomePage.css';

const HomePage = () => {
  // Navigation hook
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth context for user info
  const { user, isAuthenticated } = useAuth();
  
  // Inquiry context for adding products to inquiry
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();
  
  // Modal states
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [showBecomeSupplierModal, setShowBecomeSupplierModal] = useState(false);
  const [showFindBuyerModal, setShowFindBuyerModal] = useState(false);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [loginRedirectAction, setLoginRedirectAction] = useState(null);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);

  // Check if redirected from protected route - show login modal
  useEffect(() => {
    if (location.state?.showLogin) {
      setShowLoginModal(true);
      // Store the original path user tried to access (including search params)
      if (location.state?.from?.pathname) {
        const fullPath = location.state.from.pathname + (location.state.from.search || '');
        setLoginRedirectPath(fullPath);
      }
      // Clear the state to prevent showing modal on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Helper function to open requirement modal with login check
  const openRequirementModalWithAuth = () => {
    if (!isAuthenticated) {
      setLoginRedirectAction('requirement');
      setShowLoginModal(true);
      return;
    }
    setShowRequirementModal(true);
  };

  // Mobile slider states for each section
  const [featuredSliderIndex, setFeaturedSliderIndex] = useState(0);
  const [newProductsSliderIndex, setNewProductsSliderIndex] = useState(0);
  const [popularSliderIndex, setPopularSliderIndex] = useState(0);
  const [topSellersSliderIndex, setTopSellersSliderIndex] = useState(0);

  // Refs for slider containers
  const featuredSliderRef = React.useRef(null);
  const newProductsSliderRef = React.useRef(null);
  const popularSliderRef = React.useRef(null);
  const topSellersSliderRef = React.useRef(null);

  // Check for mobile view
  const [isMobile, setIsMobile] = useState(false);
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // State for real data from API
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addedProducts, setAddedProducts] = useState({});
  const [favoriteIds, setFavoriteIds] = useState([]);

  // ── Easy Sourcing form ──
  const [esCategories, setEsCategories] = useState([]);
  const [esForm, setEsForm] = useState({ product: '', category: '', phoneCode: '+971', phone: '' });
  const [esLoading, setEsLoading] = useState(false);
  const [esMsg, setEsMsg] = useState({ type: '', text: '' });
  const [esAgreed, setEsAgreed] = useState(false);

  useEffect(() => {
    categoriesApi.getAll()
      .then(d => setEsCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const handleEsSubmit = async (e) => {
    e.preventDefault();
    setEsMsg({ type: '', text: '' });
    if (!esForm.product.trim()) { setEsMsg({ type: 'err', text: 'Please enter a product or service' }); return; }
    if (!esForm.phone.trim()) { setEsMsg({ type: 'err', text: 'Please enter your mobile number' }); return; }
    if (!esAgreed) { setEsMsg({ type: 'err', text: 'Please agree to Terms of Use' }); return; }
    try {
      setEsLoading(true);
      await rfqApi.create({
        category: esForm.category || 'General',
        details: esForm.product,
        phoneNumber: `${esForm.phoneCode} ${esForm.phone}`,
        countryCode: esForm.phoneCode,
        fullName: user?.fullName || user?.email?.split('@')[0] || 'Website Visitor',
        email: user?.email || 'visitor@gulfconstructor.com',
        companyName: user?.companyName || 'Not Specified',
        country: user?.country || 'UAE',
        isPublicOffer: true,
        userId: user?.id || null
      });
      setEsMsg({ type: 'ok', text: 'Requirement submitted! Suppliers will contact you soon.' });
      setEsForm({ product: '', category: '', phoneCode: '+971', phone: '' });
      setEsAgreed(false);
      setTimeout(() => setEsMsg({ type: '', text: '' }), 5000);
    } catch (err) {
      setEsMsg({ type: 'err', text: err.response?.data?.error || 'Failed to submit. Please try again.' });
    } finally {
      setEsLoading(false);
    }
  };

  // Fetch favorite IDs when user logs in
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (user?.id) {
        try {
          const response = await favoritesApi.getIds(user.id);
          setFavoriteIds(response.sellerIds || []);
        } catch (error) {
          console.error('Error fetching favorite IDs:', error);
        }
      }
    };
    fetchFavoriteIds();
  }, [user?.id]);

  // Toggle favorite seller
  const handleToggleFavorite = async (sellerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      alert('Please login to save favorites');
      return;
    }

    try {
      const response = await favoritesApi.toggle(user.id, sellerId);
      if (response.isFavorited) {
        setFavoriteIds(prev => [...prev, sellerId]);
      } else {
        setFavoriteIds(prev => prev.filter(id => id !== sellerId));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Handle inquire button click
  const handleInquireClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    const seller = product.seller || {};
    const productData = {
      id: product.id,
      image: product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500",
      description: product.description || product.name,
      priceMin: product.priceMin || 0,
      priceMax: product.priceMax || 0,
      priceUnit: 'USD',
      pricePer: product.unit || 'Unit',
      moq: product.moq || '1 Unit',
      // Use seller.name as the company name (seller profile has the real company name)
      companyName: seller.name || 'Unknown Seller',
      // Use seller.userId for messaging (links seller to their user account)
      sellerId: seller.userId || product.userId,
      sellerName: seller.name || 'Unknown Seller',
      sellerAvatar: seller.logo || 'https://via.placeholder.com/52',
      country: seller.country || 'UAE',
      countryFlag: seller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
    
    if (isInInquiry(product.id)) {
      removeFromInquiry(product.id);
      setAddedProducts(prev => ({ ...prev, [product.id]: false }));
    } else {
      addToInquiry(productData);
      setAddedProducts(prev => ({ ...prev, [product.id]: true }));
      // Show "Added!" feedback for 2 seconds
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [product.id]: 'done' }));
      }, 2000);
    }
  };

  // Mobile slider scroll handler - updates dots based on scroll position
  const handleSliderScroll = (e, setIndex, productsCount) => {
    if (!isMobile) return;
    const container = e.target;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.offsetWidth / 2; // 2 cards visible at a time
    const pairIndex = Math.round(scrollLeft / cardWidth / 2); // Each page shows 2 products
    const totalPairs = Math.ceil(productsCount / 2);
    setIndex(Math.min(pairIndex, totalPairs - 1));
  };

  // Scroll to specific slide pair
  const scrollToSlide = (sliderRef, pairIndex) => {
    if (sliderRef.current) {
      const container = sliderRef.current;
      const cardWidth = container.offsetWidth / 2;
      container.scrollTo({
        left: pairIndex * cardWidth * 2,
        behavior: 'smooth'
      });
    }
  };

  // Render mobile slider dots
  const renderSliderDots = (productsCount, currentIndex, setIndex, sliderRef) => {
    if (!isMobile || productsCount <= 2) return null;
    const totalPairs = Math.ceil(productsCount / 2);
    return (
      <div className="mobile-slider-dots">
        {Array.from({ length: totalPairs }, (_, i) => (
          <button
            key={i}
            className={`mobile-slider-dot ${currentIndex === i ? 'active' : ''}`}
            onClick={() => {
              setIndex(i);
              scrollToSlide(sliderRef, i);
            }}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    );
  };

  // Fetch all data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [featuredRes, latestRes, popularRes, sellersRes, brandsRes] = await Promise.all([
          productsApi.getFeatured(),
          productsApi.getLatest(),
          productsApi.getPopular(),
          sellersApi.getTop(),
          brandsApi.getHighlighted() // Only show brands from sellers with canHighlightBrand permission
        ]);

        // Debug: Log to verify user data is coming through
        console.log('Featured products:', featuredRes);
        if (featuredRes.length > 0) {
          console.log('First product user:', featuredRes[0].user);
          console.log('First product userId:', featuredRes[0].userId);
        }

        setFeaturedProducts(featuredRes.slice(0, 5));
        setNewProducts(latestRes.slice(0, 5));
        setPopularProducts(popularRes.slice(0, 5));
        setTopSellers(sellersRes.slice(0, 4));
        // Sort brands: newest brands first (by createdAt descending)
        const sortedBrands = [...brandsRes].sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        console.log('Brands data:', sortedBrands.slice(0, 9));
        setBrands(sortedBrands.slice(0, 9));
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to render product card with real API data
  const renderFeaturedProductCard = (product) => {
    const seller = product.seller || {};
    // Use seller.name as the display name (seller profile has the real company name)
    const displayCompanyName = seller.name || 'Unknown Seller';
    const displayLogo = seller.logo || "https://via.placeholder.com/24x24?text=S";
    
    // Handle card click to navigate to product details
    const handleCardClick = (e) => {
      // Don't navigate if clicking on a button
      if (e.target.closest('button')) return;
      navigate(`/product/${product.id}`);
    };
    
    return (
      <div key={product.id} className="featured-product-card" onClick={handleCardClick} style={{ cursor: 'pointer', height: 'fit-content' }}>
        {/* Product Image Section */}
        <div className="featured-product-image-section">
          <img 
            src={product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500"} 
            alt={product.name} 
            className="featured-product-img"
          />
        </div>
        
        {/* Product Details Section */}
        <div className="featured-product-details-section" style={{ flexGrow: 0 }}>
          {/* Info Block */}
          <div className="featured-info-block">
            {/* Product Name Block */}
            <div className="featured-desc-block">
              <p className="featured-product-name">
                {product.name || 'Unknown Product'}
              </p>
              
              {/* Price Block */}
              <div className="featured-price-block">
                <div className="featured-price-row">
                  <span className="featured-price-amount">${product.priceMin} - ${product.priceMax}</span>
                  <span className="featured-price-unit">/ {product.unit}</span>
                </div>
                <p className="featured-moq">
                  <span className="featured-moq-label">(MOQ) </span>
                  <span className="featured-moq-value">{product.moq}</span>
                </p>
              </div>
            </div>
            
            {/* Badges Block */}
            <div className="featured-badges-block">
              <div className="featured-badges-row">
                {/* Membership Plan Badge - Show seller's actual plan */}
                {(() => {
                  const planConfig = getPlanConfig(seller.membershipPlan);
                  
                  return (
                    <div className="featured-global-badge" style={{ borderColor: planConfig.color }}>
                      <img 
                        src={planConfig.icon} 
                        alt={planConfig.label} 
                        className="featured-global-icon"
                      />
                      <span className="featured-global-text" style={{ color: planConfig.color }}>{planConfig.label}</span>
                    </div>
                  );
                })()}
                
                {/* Verified Badge - only show for GCC and Global plans (not Basic) */}
                {shouldShowVerifiedBadge(seller.membershipPlan) && (
                  <div className="featured-verified-badge">
                    <svg width="16" height="16" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.3129 1.49659 8.4528C1.73472 7.5623 1.02177 6.2779 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.2779 20.2653 7.5623 20.5034 8.4528C20.7329 9.3129 22 10.0663 22 11Z" fill="#29ABE2"/>
                      <path d="M15.5876 7.6015C15.1229 7.1368 14.3696 7.1368 13.9049 7.6015L9.0312 12.4751L7.00786 10.4518C6.54315 9.9871 5.7898 9.9871 5.32509 10.4518C4.86039 10.9165 4.86039 11.6698 5.32509 12.1345L8.14798 14.9574C8.39188 15.2013 8.71372 15.3168 9.0327 15.3052C9.3516 15.3168 9.6734 15.2013 9.9173 14.9574L15.5891 9.2857C16.0538 8.821 16.0538 8.0676 15.5891 7.6029L15.5876 7.6015Z" fill="white"/>
                    </svg>
                    <span className="featured-verified-text">Verified</span>
                  </div>
                )}
              </div>
              
              {/* Seller Company Name - Clickable to go to seller profile */}
              <div 
                className="featured-seller-company"
                onClick={(e) => {
                  e.stopPropagation();
                  if (seller.id) navigate(`/seller/${seller.id}`);
                }}
                style={{ cursor: seller.id ? 'pointer' : 'default' }}
                title={seller.id ? `View ${displayCompanyName} profile` : ''}
              >
                <img 
                  src={displayLogo} 
                  alt={displayCompanyName} 
                  className="featured-company-logo"
                />
                <span className="featured-company-name">{displayCompanyName}</span>
              </div>
              
              {/* Location Row */}
              <div className="featured-location-row">
                <div className="featured-flag-wrapper">
                  <img 
                    src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} 
                    alt="Flag" 
                    className="featured-flag-img"
                  />
                </div>
                <span className="featured-location-text">
                  <span className="featured-country">{(seller.country || 'UAE').replace('United Arab Emirates', 'UAE')} / </span>
                  <span className="featured-years">{seller.memberYears || 1} Year{(seller.memberYears || 1) > 1 ? 's' : ''}</span>
                </span>
              </div>
              
              {/* Business Type */}
              <span className="featured-business-text">{seller.businessType || 'Manufacturer'}</span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="featured-actions-row">
            <button 
              className={`featured-btn-inquire ${isInInquiry(product.id) ? 'added' : ''}`}
              onClick={(e) => handleInquireClick(product, e)}
            >
              {addedProducts[product.id] === true ? '✓ Added!' : isInInquiry(product.id) ? 'Remove' : 'Add To Quotation'}
            </button>
            <button 
              className="featured-btn-call"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const phone = seller.phone || seller.phoneNumber || product.phone;
                if (phone) {
                  window.location.href = `tel:${phone}`;
                } else {
                  alert('Phone number not available for this seller');
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Call</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render seller card with real API data
  const renderTopSellerCard = (seller) => {
    const productCount = seller._count?.products || 0;
    
    // Handle card click to navigate to seller profile
    const handleSellerCardClick = (e) => {
      // Don't navigate if clicking on a button (like favorite)
      if (e.target.closest('button')) return;
      navigate(`/seller/${seller.id}`);
    };
    
    return (
      <div key={seller.id} className="top-seller-card" onClick={handleSellerCardClick} style={{ cursor: 'pointer' }}>
        {/* Background Image Container */}
        <div className="top-seller-image-container">
          <img 
            src={seller.coverImage || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600"} 
            alt="Seller Background" 
            className="top-seller-bg-image"
          />
        </div>
        
        {/* Seller Content Area */}
        <div className="top-seller-content">
          {/* Avatar + Name + Bookmark Container */}
          <div className="top-seller-avatar-row">
            {seller.logo ? (
              <img src={seller.logo} alt={seller.name} className="top-seller-avatar" style={{width: 56, height: 56, borderRadius: '50%', objectFit: 'cover'}} />
            ) : (
              <svg className="top-seller-avatar" width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="27.5" fill="#E6EBEF" stroke="#D3D3D3"/>
                <rect x="15" y="18" width="26" height="20" rx="2" fill="#1F396C"/>
                <circle cx="28" cy="28" r="6" fill="white"/>
              </svg>
            )}
            <div className="top-seller-name-wrap">
              <span className="top-seller-name">{seller.name}</span>
            </div>
            {/* Bookmark Icon - Next to company name */}
            <button 
              className={`top-seller-bookmark-btn ${favoriteIds.includes(seller.id) ? 'favorited' : ''}`}
              onClick={(e) => handleToggleFavorite(seller.id, e)}
              title={favoriteIds.includes(seller.id) ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z" 
                  fill={favoriteIds.includes(seller.id) ? '#1F396C' : '#969696'}
                />
              </svg>
            </button>
          </div>
          
          {/* Info Section - Badges, Location, Business Type */}
          <div className="top-seller-info-section">
            {/* Badges Row */}
            <div className="top-seller-badges-row">
              {/* Membership Plan Badge - Always show seller's actual plan */}
              {(() => {
                const plan = seller.membershipPlan || 'Basic';
                const planLower = plan.toLowerCase();
                
                // Icons for different plans
                const basicIcon = 'https://api.builder.io/api/v1/image/assets/TEMP/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea?width=44';
                const gccIcon = 'https://api.builder.io/api/v1/image/assets/TEMP/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c?width=44';
                const globalIcon = 'https://api.builder.io/api/v1/image/assets/TEMP/050559d54da8d35ba6d11841efdb737d4c3ae660?width=44';
                
                let config;
                
                // Check for GCC/Premium plans
                if (planLower.includes('gcc') || planLower.includes('premium') || planLower.includes('gold')) {
                  config = { color: '#009D0A', icon: gccIcon, label: 'GCC Business' };
                }
                // Check for Global/Enterprise plans
                else if (planLower.includes('global') || planLower.includes('enterprise') || planLower.includes('platinum')) {
                  config = { color: '#FF6B00', icon: globalIcon, label: 'Global' };
                }
                // Default to Basic
                else {
                  config = { color: '#6D6D6D', icon: basicIcon, label: 'Basic' };
                }
                
                return (
                  <div className="top-seller-global-badge">
                    {config.icon && (
                      <img src={config.icon} alt={config.label} className="top-seller-global-icon" />
                    )}
                    <span className="top-seller-global-text" style={{ color: config.color }}>{config.label}</span>
                  </div>
                );
              })()}
              {shouldShowVerifiedBadge(seller.membershipPlan) && (
                <svg className="top-seller-verified-badge" width="96" height="30" viewBox="0 0 96 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 15C22 15.9323 20.7329 16.6871 20.5034 17.5472C20.2653 18.4377 20.9782 19.7221 20.528 20.5015C20.0705 21.2923 18.597 21.314 17.9562 21.9562C17.314 22.5984 17.2923 24.0705 16.5014 24.528C15.7221 24.9782 14.4377 24.2653 13.5472 24.5034C12.6871 24.7329 11.9337 26 11 26C10.0663 26 9.31291 24.7329 8.45276 24.5034C7.56231 24.2653 6.27788 24.9782 5.49856 24.528C4.70769 24.0705 4.68605 22.597 4.04382 21.9562C3.4016 21.3154 1.92954 21.2923 1.47205 20.5015C1.02177 19.7221 1.73472 18.4377 1.49659 17.5472C1.26712 16.6871 0 15.9338 0 15C0 14.0663 1.26712 13.3129 1.49659 12.4528C1.73472 11.5623 1.02177 10.2779 1.47205 9.49857C1.92954 8.7077 3.40305 8.68605 4.04382 8.04383C4.68605 7.40161 4.70769 5.92955 5.49856 5.47206C6.27788 5.02178 7.56231 5.73472 8.45276 5.49659C9.31291 5.26712 10.0663 4 11 4C11.9337 4 12.6871 5.26712 13.5472 5.49659C14.4377 5.73472 15.7221 5.02178 16.5014 5.47206C17.2923 5.92955 17.314 7.40305 17.9562 8.04383C18.5984 8.68461 20.0705 8.7077 20.528 9.49857C20.9782 10.2779 20.2653 11.5623 20.5034 12.4528C20.7329 13.3129 22 14.0663 22 15Z" fill="#29ABE2"/>
                  <path d="M16.5876 11.6015C16.1229 11.1368 15.3696 11.1368 14.9049 11.6015L10.0312 16.4751L8.00786 14.4518C7.54315 13.9871 6.7898 13.9871 6.32509 14.4518C5.86039 14.9165 5.86039 15.6698 6.32509 16.1345L9.14798 18.9574C9.39188 19.2013 9.71372 19.3168 10.0327 19.3052C10.3516 19.3168 10.6734 19.2013 10.9173 18.9574L16.5891 13.2857C17.0538 12.821 17.0538 12.0676 16.5891 11.6029L16.5876 11.6015Z" fill="white"/>
                  <text fill="#29ABE2" xmlSpace="preserve" style={{whiteSpace: 'pre'}} fontFamily="Poppins" fontSize="17" letterSpacing="0em"><tspan x="27" y="20.95">Verified</tspan></text>
                </svg>
              )}
            </div>
            
            {/* Location with Flag */}
            <div className="top-seller-location-wrap">
              <div className="top-seller-location-row">
                <div className="top-seller-flag-container">
                  <img src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} alt="Flag" className="top-seller-flag-img" />
                </div>
                <span className="top-seller-location-text">
                  <span className="top-seller-country">{seller.country} / </span>
                  <span className="top-seller-years">{seller.memberYears} Year{seller.memberYears > 1 ? 's' : ''}</span>
                </span>
              </div>
            </div>
            
            {/* Business Type */}
            <div className="top-seller-business-wrap">
              <div className="top-seller-business-inner">
                <span className="top-seller-business-type">{seller.businessType}</span>
              </div>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="top-seller-stats-row">
            <div 
              className="top-seller-stat-item" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/seller/${seller.id}?tab=reviews`);
              }}
              style={{ cursor: 'pointer' }}
              title="View seller reviews"
            >
              <span className="top-seller-stat-text">{seller.reviews || 0} Reviews</span>
            </div>
            <div 
              className="top-seller-stat-item"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/supplier/${seller.id}/products`);
              }}
              style={{ cursor: 'pointer' }}
              title="View seller products"
            >
              <span className="top-seller-stat-text">{productCount} Products</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home-page">
      <Header isHomePage={true} />
      <main className="main-content">
        
        {/* ===== HERO SECTION ===== */}
        <section className="hero-section">
          <div className="hero-container homepage-grid-layout">
            {/* Category Sidebar */}
            <CategorySidebar />
            
            {/* Main Banner */}
            <div className="hero-banner">
              <img 
                src="https://api.builder.io/api/v1/image/assets/TEMP/3a7973ec52392114c2fdeec018d7f80fb0cb215e?width=1540" 
                alt="Banner" 
                className="hero-image" 
              />
            </div>
            
            {/* CTA Cards - Hidden on Mobile via CSS class */}
            <div className="hero-cta-cards desktop-only-cta">
              <div className="cta-card">
                <div className="cta-card-content">
                  <h3 className="cta-title">TELL SUPPLIERS<br/>WHAT YOU NEED</h3>
                  <button className="cta-button primary" onClick={openRequirementModalWithAuth}>Send your Requirement</button>
                </div>
              </div>
              <div className="cta-card">
                <div className="cta-card-content">
                  <h3 className="cta-title">GROW YOUR<br/>BUSINESS 10X</h3>
                  <button className="cta-button primary" onClick={() => setShowBecomeSupplierModal(true)}>Become a Supplier</button>
                </div>
              </div>
              <div className="cta-card">
                <div className="cta-card-content">
                  <h3 className="cta-title">FIND RIGHT<br/>BUYERS NOW</h3>
                  <button className="cta-button primary" onClick={() => setShowFindBuyerModal(true)}>Find Buyer</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== MOBILE QUICK ACTIONS - Mobile Only (CSS controlled) ===== */}
        <section className="mobile-quick-actions-section">
          <div className="mobile-quick-actions-container">
            <div className="mobile-quick-action-item" onClick={() => navigate('/all-categories')}>
              <div className="mobile-quick-action-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="4" width="10" height="10" rx="2" stroke="#1F396C" strokeWidth="2"/>
                  <rect x="18" y="4" width="10" height="10" rx="2" stroke="#1F396C" strokeWidth="2"/>
                  <rect x="4" y="18" width="10" height="10" rx="2" stroke="#1F396C" strokeWidth="2"/>
                  <rect x="18" y="18" width="10" height="10" rx="2" stroke="#1F396C" strokeWidth="2"/>
                  <circle cx="9" cy="9" r="2" fill="#1F396C"/>
                  <circle cx="23" cy="9" r="2" fill="#1F396C"/>
                  <circle cx="9" cy="23" r="2" fill="#1F396C"/>
                  <circle cx="23" cy="23" r="2" fill="#1F396C"/>
                </svg>
              </div>
              <span className="mobile-quick-action-label">All<br/>Categories</span>
            </div>
            
            <div className="mobile-quick-action-item" onClick={openRequirementModalWithAuth}>
              <div className="mobile-quick-action-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="12" stroke="#1F396C" strokeWidth="2"/>
                  <circle cx="16" cy="16" r="4" stroke="#1F396C" strokeWidth="2"/>
                  <path d="M16 4V8" stroke="#1F396C" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 24V28" stroke="#1F396C" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M4 16H8" stroke="#1F396C" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M24 16H28" stroke="#1F396C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="mobile-quick-action-label">Post Your<br/>Request</span>
            </div>
            
            <div className="mobile-quick-action-item" onClick={() => setShowBecomeSupplierModal(true)}>
              <div className="mobile-quick-action-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 8C20 10.2091 18.2091 12 16 12C13.7909 12 12 10.2091 12 8C12 5.79086 13.7909 4 16 4C18.2091 4 20 5.79086 20 8Z" stroke="#1F396C" strokeWidth="2"/>
                  <path d="M8 28V24C8 21.7909 9.79086 20 12 20H20C22.2091 20 24 21.7909 24 24V28" stroke="#1F396C" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M22 10L26 14L22 18" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M26 14H20" stroke="#1F396C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="mobile-quick-action-label">Become<br/>A Supplier</span>
            </div>
            
            <div className="mobile-quick-action-item" onClick={() => setShowComingSoonModal(true)}>
              <div className="mobile-quick-action-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="6" width="24" height="20" rx="2" stroke="#1F396C" strokeWidth="2"/>
                  <path d="M4 12H28" stroke="#1F396C" strokeWidth="2"/>
                  <rect x="8" y="16" width="6" height="6" rx="1" fill="#1F396C"/>
                  <rect x="18" y="16" width="6" height="6" rx="1" stroke="#1F396C" strokeWidth="1.5"/>
                </svg>
              </div>
              <span className="mobile-quick-action-label">Buyer<br/>Directory</span>
            </div>
          </div>
        </section>

        {/* ===== FEATURED PRODUCTS SECTION - Figma Pixel Perfect ===== */}
        <section className="section-outer-wrapper" style={{ paddingBottom: 0 }}>
          <div className="section-white-box" style={{ paddingBottom: '16px', gap: '16px' }}>
            <div className="featured-section-header">
              <span className="featured-section-title">Featured Products</span>
              <Link to="/featured-products" className="featured-view-all-link">
                <span>View All</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.75 3.5C12.9822 3.5 13.205 3.59268 13.3691 3.75684L18.9932 9.38086H18.9941C19.0754 9.46205 19.1396 9.55892 19.1836 9.66504C19.2276 9.77116 19.2509 9.88513 19.251 10C19.251 10.1149 19.2275 10.2288 19.1836 10.335C19.1396 10.4411 19.0754 10.5379 18.9941 10.6191L13.3691 16.2441C13.205 16.4083 12.9822 16.501 12.75 16.501C12.5178 16.501 12.295 16.4083 12.1309 16.2441C11.9668 16.08 11.875 15.8571 11.875 15.625C11.8751 15.3931 11.967 15.1709 12.1309 15.0068L16.2627 10.875H4.625C4.39294 10.875 4.16995 10.7832 4.00586 10.6191C3.84187 10.4551 3.75 10.232 3.75 10C3.75009 9.76821 3.84204 9.54582 4.00586 9.38184C4.16995 9.21774 4.39294 9.125 4.625 9.125H16.2627L12.1309 4.99414C11.9668 4.83 11.875 4.60707 11.875 4.375C11.8751 4.14312 11.967 3.92088 12.1309 3.75684C12.295 3.59268 12.5178 3.5 12.75 3.5Z" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="0.5"/>
                </svg>
              </Link>
            </div>
            <div 
              className="featured-products-grid"
              ref={featuredSliderRef}
              onScroll={(e) => handleSliderScroll(e, setFeaturedSliderIndex, featuredProducts.length)}
              style={{ alignItems: 'start' }}
            >
            {loading ? (
              <div className="loading-placeholder">Loading products...</div>
            ) : featuredProducts.length > 0 ? (
              featuredProducts.map((product) => renderFeaturedProductCard(product))
            ) : (
              <div className="no-data-placeholder">No featured products available</div>
            )}
            </div>
            {renderSliderDots(featuredProducts.length, featuredSliderIndex, setFeaturedSliderIndex, featuredSliderRef)}
          </div>
        </section>

        {/* ===== PROMO BANNER SECTION ===== */}
        <section className="banner-section">
          <img 
            src="https://api.builder.io/api/v1/image/assets/TEMP/3c016f814b1ec2f6de1b4fadc5a4973ef87328da?width=2712" 
            alt="Promo Banner" 
            className="promo-banner" 
          />
        </section>

        {/* ===== TOP SELLER SECTION - Figma Pixel Perfect ===== */}
        <section className="section-outer-wrapper">
          <div className="section-white-box">
            <div className="top-seller-header">
              <span className="top-seller-title">Top Seller</span>
              <Link to="/top-seller" className="top-seller-view-all">
                <span className="top-seller-view-all-text">View All</span>
                <svg className="top-seller-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.75 3.5C12.9822 3.5 13.205 3.59268 13.3691 3.75684L18.9932 9.38086H18.9941C19.0754 9.46205 19.1396 9.55892 19.1836 9.66504C19.2276 9.77116 19.2509 9.88513 19.251 10C19.251 10.1149 19.2275 10.2288 19.1836 10.335C19.1396 10.4411 19.0754 10.5379 18.9941 10.6191L13.3691 16.2441C13.205 16.4083 12.9822 16.501 12.75 16.501C12.5178 16.501 12.295 16.4083 12.1309 16.2441C11.9668 16.08 11.875 15.8571 11.875 15.625C11.8751 15.3931 11.967 15.1709 12.1309 15.0068L16.2627 10.875H4.625C4.39294 10.875 4.16995 10.7832 4.00586 10.6191C3.84187 10.4551 3.75 10.232 3.75 10C3.75009 9.76821 3.84204 9.54582 4.00586 9.38184C4.16995 9.21774 4.39294 9.125 4.625 9.125H16.2627L12.1309 4.99414C11.9668 4.83 11.875 4.60707 11.875 4.375C11.8751 4.14312 11.967 3.92088 12.1309 3.75684C12.295 3.59268 12.5178 3.5 12.75 3.5Z" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="0.5"/>
                </svg>
              </Link>
            </div>
            <div 
              className="top-seller-grid"
              ref={topSellersSliderRef}
              onScroll={(e) => handleSliderScroll(e, setTopSellersSliderIndex, topSellers.length)}
            >
              {loading ? (
                <div className="loading-placeholder">Loading sellers...</div>
              ) : topSellers.length > 0 ? (
                topSellers.map((seller) => renderTopSellerCard(seller))
              ) : (
                <div className="no-data-placeholder">No top sellers available</div>
              )}
            </div>
            {renderSliderDots(topSellers.length, topSellersSliderIndex, setTopSellersSliderIndex, topSellersSliderRef)}
          </div>
        </section>

        {/* ===== AD BANNERS SECTION ===== */}
        <section className="ad-banners-section">
          <div className="ad-banners">
            <img 
              src="https://api.builder.io/api/v1/image/assets/TEMP/653f3a9a97df5ee947e40a8820e0013b5a7d393f?width=1344" 
              alt="Ad Banner 1" 
              className="ad-banner" 
            />
            <img 
              src="https://api.builder.io/api/v1/image/assets/TEMP/0b2f255a28b8ecb87408a09c037273e56044e4bf?width=1344" 
              alt="Ad Banner 2" 
              className="ad-banner" 
            />
          </div>
        </section>

        {/* ===== BRANDS SECTION - Figma Pixel Perfect ===== */}
        <section className="brand-section-figma">
          <div className="brand-container-figma">
            {/* Header Row */}
            <div className="brand-header-figma">
              <span className="brand-title-figma">Brand</span>
              <Link to="/all-brands" className="brand-view-all-figma">
                <span>View All</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.75 3.5C12.9822 3.5 13.205 3.59268 13.3691 3.75684L18.9932 9.38086H18.9941C19.0754 9.46205 19.1396 9.55892 19.1836 9.66504C19.2276 9.77116 19.2509 9.88513 19.251 10C19.251 10.1149 19.2275 10.2288 19.1836 10.335C19.1396 10.4411 19.0754 10.5379 18.9941 10.6191L13.3691 16.2441C13.205 16.4083 12.9822 16.501 12.75 16.501C12.5178 16.501 12.295 16.4083 12.1309 16.2441C11.9668 16.08 11.875 15.8571 11.875 15.625C11.8751 15.3931 11.967 15.1709 12.1309 15.0068L16.2627 10.875H4.625C4.39294 10.875 4.16995 10.7832 4.00586 10.6191C3.84187 10.4551 3.75 10.232 3.75 10C3.75009 9.76821 3.84204 9.54582 4.00586 9.38184C4.16995 9.21774 4.39294 9.125 4.625 9.125H16.2627L12.1309 4.99414C11.9668 4.83 11.875 4.60707 11.875 4.375C11.8751 4.14312 11.967 3.92088 12.1309 3.75684C12.295 3.59268 12.5178 3.5 12.75 3.5Z" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="0.5"/>
                </svg>
              </Link>
            </div>
            {/* Logos Row - 9 circular brand logos */}
            <div className="brand-logos-row-figma">
              {loading ? (
                <div className="loading-placeholder">Loading brands...</div>
              ) : brands.length > 0 ? (
                brands.map((brand) => {
                  // Generate initials and color for placeholder
                  const initials = brand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                  const colors = ['#00BCD4', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#3F51B5', '#009688', '#FF5722', '#673AB7'];
                  const bgColor = colors[brand.id % colors.length];
                  
                  // Only use logo if it's a proper uploaded logo (starts with /uploads/), not stock photos
                  const isValidLogo = brand.logo && 
                    (brand.logo.startsWith('/uploads/') || brand.logo.startsWith('http://localhost'));
                  const logoUrl = isValidLogo 
                    ? (brand.logo.startsWith('http') ? brand.logo : `${API_BASE}${brand.logo}`)
                    : null;
                  
                  // Navigate to seller profile if it's a highlighted seller brand
                  const handleBrandClick = () => {
                    if (brand.type === 'seller') {
                      navigate(`/supplier/${brand.id}/profile`);
                    } else {
                      navigate(`/products?brand=${brand.id}`);
                    }
                  };
                  
                  return (
                    <div 
                      key={brand.id} 
                      className="brand-logo-figma" 
                      title={brand.name}
                      onClick={handleBrandClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {logoUrl ? (
                        <img 
                          src={logoUrl}
                          alt={brand.name}
                          style={{ width: '80%', height: '80%', objectFit: 'contain' }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: bgColor,
                          borderRadius: '50%',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '24px'
                        }}>
                          {initials}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="no-data-placeholder">No brands available</div>
              )}
            </div>
          </div>
        </section>

        {/* ===== NEW PRODUCTS SECTION - Figma Pixel Perfect ===== */}
        <section className="section-outer-wrapper">
          <div className="section-white-box">
            <div className="featured-section-header">
              <span className="featured-section-title">New Products</span>
              <Link to="/products" className="featured-view-all-link">
                <span>View All</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.75 3.5C12.9822 3.5 13.205 3.59268 13.3691 3.75684L18.9932 9.38086H18.9941C19.0754 9.46205 19.1396 9.55892 19.1836 9.66504C19.2276 9.77116 19.2509 9.88513 19.251 10C19.251 10.1149 19.2275 10.2288 19.1836 10.335C19.1396 10.4411 19.0754 10.5379 18.9941 10.6191L13.3691 16.2441C13.205 16.4083 12.9822 16.501 12.75 16.501C12.5178 16.501 12.295 16.4083 12.1309 16.2441C11.9668 16.08 11.875 15.8571 11.875 15.625C11.8751 15.3931 11.967 15.1709 12.1309 15.0068L16.2627 10.875H4.625C4.39294 10.875 4.16995 10.7832 4.00586 10.6191C3.84187 10.4551 3.75 10.232 3.75 10C3.75009 9.76821 3.84204 9.54582 4.00586 9.38184C4.16995 9.21774 4.39294 9.125 4.625 9.125H16.2627L12.1309 4.99414C11.9668 4.83 11.875 4.60707 11.875 4.375C11.8751 4.14312 11.967 3.92088 12.1309 3.75684C12.295 3.59268 12.5178 3.5 12.75 3.5Z" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="0.5"/>
                </svg>
              </Link>
            </div>
            <div 
              className="featured-products-grid"
              ref={newProductsSliderRef}
              onScroll={(e) => handleSliderScroll(e, setNewProductsSliderIndex, newProducts.length)}
            >
            {loading ? (
              <div className="loading-placeholder">Loading products...</div>
            ) : newProducts.length > 0 ? (
              newProducts.map((product) => renderFeaturedProductCard(product))
            ) : (
              <div className="no-data-placeholder">No new products available</div>
            )}
            </div>
            {renderSliderDots(newProducts.length, newProductsSliderIndex, setNewProductsSliderIndex, newProductsSliderRef)}
          </div>
        </section>

        {/* ===== NEW PRODUCTS PROMO BANNER ===== */}
        <section className="banner-section">
          <img 
            src="https://api.builder.io/api/v1/image/assets/TEMP/3c016f814b1ec2f6de1b4fadc5a4973ef87328da?width=2712" 
            alt="Promo Banner" 
            className="promo-banner" 
          />
        </section>

        {/* ===== POPULAR PRODUCTS SECTION - Figma Pixel Perfect ===== */}
        <section className="section-outer-wrapper">
          <div className="section-white-box">
            <div className="featured-section-header">
              <span className="featured-section-title">Popular Products</span>
              <Link to="/products" className="featured-view-all-link">
                <span>View All</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.75 3.5C12.9822 3.5 13.205 3.59268 13.3691 3.75684L18.9932 9.38086H18.9941C19.0754 9.46205 19.1396 9.55892 19.1836 9.66504C19.2276 9.77116 19.2509 9.88513 19.251 10C19.251 10.1149 19.2275 10.2288 19.1836 10.335C19.1396 10.4411 19.0754 10.5379 18.9941 10.6191L13.3691 16.2441C13.205 16.4083 12.9822 16.501 12.75 16.501C12.5178 16.501 12.295 16.4083 12.1309 16.2441C11.9668 16.08 11.875 15.8571 11.875 15.625C11.8751 15.3931 11.967 15.1709 12.1309 15.0068L16.2627 10.875H4.625C4.39294 10.875 4.16995 10.7832 4.00586 10.6191C3.84187 10.4551 3.75 10.232 3.75 10C3.75009 9.76821 3.84204 9.54582 4.00586 9.38184C4.16995 9.21774 4.39294 9.125 4.625 9.125H16.2627L12.1309 4.99414C11.9668 4.83 11.875 4.60707 11.875 4.375C11.8751 4.14312 11.967 3.92088 12.1309 3.75684C12.295 3.59268 12.5178 3.5 12.75 3.5Z" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="0.5"/>
                </svg>
              </Link>
            </div>
            <div 
              className="featured-products-grid"
              ref={popularSliderRef}
              onScroll={(e) => handleSliderScroll(e, setPopularSliderIndex, popularProducts.length)}
            >
            {loading ? (
              <div className="loading-placeholder">Loading products...</div>
            ) : popularProducts.length > 0 ? (
              popularProducts.map((product) => renderFeaturedProductCard(product))
            ) : (
              <div className="no-data-placeholder">No popular products available</div>
            )}
            </div>
            {renderSliderDots(popularProducts.length, popularSliderIndex, setPopularSliderIndex, popularSliderRef)}
          </div>
        </section>

        {/* ===== EASY SOURCING SECTION ===== */}
        <section className="es-section">
          <div className="es-wrapper">
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/a1ef1baa0da7d3db74825fea94018975265092e7?width=2712"
              alt="Easy Sourcing"
              className="es-bg-img"
            />
          </div>
        </section>

      </main>
      <Footer />
      
      {/* Modals */}
      <RequirementModal 
        isOpen={showRequirementModal} 
        onClose={() => setShowRequirementModal(false)} 
      />
      <BecomeSupplierModal 
        isOpen={showBecomeSupplierModal} 
        onClose={() => setShowBecomeSupplierModal(false)}
        onLoginClick={() => {
          setShowBecomeSupplierModal(false);
          setShowLoginModal(true);
        }}
        onVerifyEmail={(email) => {
          setShowBecomeSupplierModal(false);
          // User is registered and logged in, redirect to documents submit
          navigate('/documents-submit');
        }}
      />
      <FindBuyerModal 
        isOpen={showFindBuyerModal} 
        onClose={() => setShowFindBuyerModal(false)}
        onSave={(data) => {
          console.log('Find Buyer data:', data);
          setShowFindBuyerModal(false);
          // TEMPORARILY DISABLED - Navigate to buyer directory
          // if (data.tagline) {
          //   navigate(`/buyer-directory?search=${encodeURIComponent(data.tagline)}`);
          // } else {
          //   navigate('/buyer-directory');
          // }
        }}
      />
      <ProductDetailsModal 
        isOpen={showProductDetailsModal} 
        onClose={() => {
          setShowProductDetailsModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct ? {
          id: selectedProduct.id,
          image: selectedProduct.image,
          description: selectedProduct.description || selectedProduct.name,
          priceMin: selectedProduct.priceMin,
          priceMax: selectedProduct.priceMax,
          priceUnit: 'USD',
          pricePer: selectedProduct.unit || 'Unit',
          moq: selectedProduct.moq || '1 Unit',
          companyName: selectedProduct.seller?.name || 'Unknown Seller',
          sellerId: selectedProduct.seller?.userId || selectedProduct.userId,
          sellerAvatar: selectedProduct.seller?.logo,
          country: selectedProduct.seller?.country || 'UAE',
          countryFlag: selectedProduct.seller?.countryFlag,
          memberYears: selectedProduct.seller?.memberYears || 1,
          businessType: selectedProduct.seller?.businessType || 'Manufacturer',
          isVerified: selectedProduct.seller?.isVerified,
          isGlobal: selectedProduct.seller?.isGlobal,
        } : null}
      />
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          // If user was trying to open requirement modal, open it after login
          if (loginRedirectAction === 'requirement' && isAuthenticated) {
            setShowRequirementModal(true);
          }
          setLoginRedirectAction(null);
          setLoginRedirectPath(null);
        }}
        onRegisterClick={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
        redirectTo={loginRedirectPath}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onLoginClick={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
      
      {/* Coming Soon Modal */}
      {showComingSoonModal && (
        <div className="coming-soon-modal-overlay" onClick={() => setShowComingSoonModal(false)}>
          <div className="coming-soon-modal" onClick={(e) => e.stopPropagation()}>
            <button className="coming-soon-close" onClick={() => setShowComingSoonModal(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="coming-soon-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="28" stroke="#1F396C" strokeWidth="3"/>
                <path d="M32 18V34L42 40" stroke="#1F396C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="coming-soon-title">Coming Soon!</h3>
            <p className="coming-soon-text">Buyer Directory feature is under development. Stay tuned for updates!</p>
            <button className="coming-soon-btn" onClick={() => setShowComingSoonModal(false)}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
